"use client";

import { useEffect, useState } from "react";
import { Download, PlayCircle, RotateCcw, Target } from "lucide-react";

import { BUS_QUIZ_MISTAKES_PREFIX } from "@/app/components/quizzes/bus-quiz-storage";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import type { H2StudyRoute } from "@/lib/study/h2-study-types";
import { saveStudyProgress } from "@/lib/study/study-progress";

export default function H2ArticleStudyRouter({
  route,
  className = "mb-6",
}: {
  route: H2StudyRoute;
  className?: string;
}) {
  const [hasMistakes, setHasMistakes] = useState(false);

  useEffect(() => {
    if (!route.quizId) return;
    try {
      const raw = window.localStorage.getItem(
        `${BUS_QUIZ_MISTAKES_PREFIX}${route.quizId}`
      );
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      setHasMistakes(Array.isArray(parsed) && parsed.length > 0);
    } catch {
      setHasMistakes(false);
    }
  }, [route.quizId]);

  const recordSelection = (
    task: "download" | "watch" | "practise" | "review",
    href: string
  ) => {
    const activity =
      task === "watch"
        ? "video"
        : task === "practise"
        ? "quiz"
        : task === "review"
        ? "mistakes"
        : "note";
    if (task !== "download") {
      saveStudyProgress({
        subject: route.subject,
        subjectLabel: route.subjectLabel,
        topicId: route.topicId,
        topicLabel: route.topicLabel,
        activity,
        resumeHref: href,
        noteHref: route.noteHref,
        quizHref: route.quizHref,
        videoHref: route.videoHref,
      });
    }
    trackJourneyEvent("study_resource_selected", {
      journey_name: "h2_study_loop",
      measurement_contract: "h2_study_v1",
      study_subject: route.subject,
      study_topic: route.topicId,
      study_task: task,
      resource_type:
        task === "practise"
          ? "quiz"
          : task === "watch"
          ? "video"
          : task === "download"
          ? "pdf"
          : "mistakes",
      ...(task === "watch" && route.videoMatchQuality
        ? { match_quality: route.videoMatchQuality }
        : {}),
      target_url: href,
      source_surface: "h2_note_opening",
    });
  };

  const practiseHref = route.quizHref ?? route.quizHubHref;
  const watchHref = route.videoHref ?? route.videoHubHref;
  const reviewHref = route.quizHref ? `${route.quizHref}?review=missed` : null;
  const downloadId =
    route.downloadHref
      .split("/")
      .pop()
      ?.replace(/\.pdf$/, "") ?? route.topicId;

  const actions = [
    {
      key: "download",
      title: "Download PDF",
      href: route.downloadHref,
      Icon: Download,
    },
    {
      key: "practise",
      title: "MCQ Quiz",
      href: practiseHref,
      Icon: Target,
    },
    {
      key: "watch",
      title:
        route.videoMatchQuality === "related"
          ? "Watch Related Video"
          : "Watch Video",
      href: watchHref,
      Icon: PlayCircle,
    },
    ...(hasMistakes && reviewHref
      ? [
          {
            key: "review",
            title: "Review mistakes",
            href: reviewHref,
            Icon: RotateCcw,
          },
        ]
      : []),
  ] as const;

  return (
    <nav
      aria-label="Study this topic"
      className={`not-prose ${className}`}
      data-ui-role="study-task-router"
      data-ui-priority="primary"
    >
      <ul className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        {actions.map(({ key, title, href, Icon }) => (
          <li key={key} className={key === "download" ? "col-span-2" : ""}>
            <a
              href={href}
              onClick={() =>
                recordSelection(
                  key as Parameters<typeof recordSelection>[0],
                  href
                )
              }
              data-ga-event={
                key === "download" ? "resource_download" : undefined
              }
              data-ga-category={key === "download" ? "blog_article" : undefined}
              data-ga-journey={
                key === "download" ? "blog_article_download" : undefined
              }
              data-ga-label={key === "download" ? "article_header" : undefined}
              data-ga-action={key === "download" ? "download_pdf" : undefined}
              data-ga-source-surface={
                key === "download" ? "article_header_download" : undefined
              }
              data-ga-target-stage={key === "download" ? "resource" : undefined}
              data-ga-target-type={key === "download" ? "download" : undefined}
              data-ga-target-id={
                key === "download" ? `${downloadId}_pdf` : undefined
              }
              data-ga-cta-family={
                key === "download" ? "resource_download" : undefined
              }
              data-ga-commercial-intent={
                key === "download" ? "assist" : undefined
              }
              data-ga-value={key === "download" ? downloadId : undefined}
              className={`group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold no-underline shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:w-auto ${
                key === "download"
                  ? "bg-eclat-700 text-white hover:bg-eclat-800 hover:text-white"
                  : "border border-slate-200 bg-white text-blue-900 hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-900"
              }`}
            >
              <Icon
                className={`h-4 w-4 ${
                  key === "download" ? "text-white" : "text-blue-700"
                }`}
                aria-hidden="true"
              />
              <span className="group-hover:underline">{title}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
